import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { getGithubRepos } from '../endpoints/github/repos_GET.schema';
import { getGithubContents } from '../endpoints/github/contents_GET.schema';
import { getGithubFile } from '../endpoints/github/file_GET.schema';
import { getGithubBranches } from '../endpoints/github/branches_GET.schema';
import {
  createGithubCommit,
  InputType as CommitInputType,
} from '../endpoints/github/commit_POST.schema';
import {
  generateCommitMessage,
  InputType as GenerateCommitInputType,
} from '../endpoints/github/generate-commit-message_POST.schema';
import { getGithubCommits } from '../endpoints/github/commits_GET.schema';

// Don't retry on 401/403 — token is expired or missing, retrying won't help
const noRetryOnAuth = (failureCount: number, error: Error) => {
  if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('Unauthorized')) {
    return false;
  }
  return failureCount < 2;
};

export const useGithubRepos = (page = 1, perPage = 30, enabled = true) => {
  return useQuery({
    queryKey: ['github', 'repos', page, perPage],
    queryFn: () => getGithubRepos({ page, per_page: perPage }),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: noRetryOnAuth,
  });
};

export const useGithubContents = (repo: string | undefined, path: string = '', branch?: string) => {
  return useQuery({
    queryKey: ['github', 'contents', repo, path, branch],
    queryFn: () => {
      if (!repo) throw new Error('Repo is required');
      return getGithubContents({ repo, path, branch });
    },
    enabled: !!repo,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: noRetryOnAuth,
  });
};

export const useGithubFile = (
  repo: string | undefined,
  path: string,
  branch?: string,
  enabled = false,
) => {
  return useQuery({
    queryKey: ['github', 'file', repo, path, branch],
    queryFn: () => {
      if (!repo) throw new Error('Repo is required');
      return getGithubFile({ repo, path, branch });
    },
    enabled: !!repo && enabled,
    staleTime: 1000 * 60 * 60, // 1 hour (files don't change that often during import session)
    retry: noRetryOnAuth,
  });
};

export const useGithubBranches = (repo: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: ['github', 'branches', repo],
    queryFn: () => {
      if (!repo) throw new Error('Repo is required');
      return getGithubBranches({ repo });
    },
    enabled: !!repo && enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: noRetryOnAuth,
  });
};

export const useGithubCommitHistory = (
  repo: string | undefined,
  branch?: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['github', 'commits', repo, branch],
    queryFn: () => {
      if (!repo) throw new Error('Repo is required');
      return getGithubCommits({ repo, branch, per_page: 50 });
    },
    enabled: !!repo && enabled,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: noRetryOnAuth,
  });
};

export const useGithubCommit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CommitInputType) => createGithubCommit(input),
    onSuccess: () => {
      // Invalidate any cached GitHub data after commit
      queryClient.invalidateQueries({ queryKey: ['github'] });
    },
  });
};

export const useGenerateCommitMessage = () => {
  return useMutation({
    mutationFn: (input: GenerateCommitInputType) => generateCommitMessage(input),
  });
};
